/**
 * tokens.ts — CSS 색상 토큰의 JS 런타임 어댑터 (기존 static/js/lib/tokens.js 포팅)
 *
 * CSS 가 단일 SSOT 이며 JS 는 getComputedStyle 로 값을 읽는 소비자.
 * 팔레트 수정은 tokens.css 한 곳에서만, 이 모듈은 변경 없음.
 *
 * 사용:
 *   import { token } from '@/lib/tokens';
 *   ctx.fillStyle = token('surface-page');
 *
 * 결과는 프로세스 생명주기 동안 캐시된다. 테마 전환 등으로 무효화가
 * 필요하면 invalidateTokenCache() 를 호출.
 */

const _cache = new Map<string, string>();

/**
 * CSS 변수 값을 읽어 반환한다. '--' 접두사는 자동으로 붙인다.
 * 미정의 변수는 console.warn 후 빈 문자열을 반환한다.
 */
export function token(name: string): string {
  const cached = _cache.get(name);
  if (cached !== undefined) return cached;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return '';
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();
  if (!raw) {
    // eslint-disable-next-line no-console
    console.warn(`[tokens] --${name} 가 정의되지 않았습니다 (tokens.css 확인)`);
  }
  _cache.set(name, raw);
  return raw;
}

/**
 * 토큰을 alphaTab/canvas 처럼 CSS color() / color-mix() 를 파싱하지 못하는
 * 소비자에게 전달하기 위한 형태로 반환한다. 8-bit rgba 로 강제 normalize.
 */
export function tokenColor(name: string): string {
  const key = `color:${name}`;
  const cached = _cache.get(key);
  if (cached !== undefined) return cached;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return '';
  }

  const probe = document.createElement('span');
  probe.style.color = `var(--${name})`;
  probe.style.display = 'none';
  document.documentElement.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();

  if (!resolved) {
    // eslint-disable-next-line no-console
    console.warn(`[tokens] tokenColor('${name}') 해석 실패`);
    _cache.set(key, '');
    return '';
  }

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    _cache.set(key, resolved);
    return resolved;
  }
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = resolved;
  ctx.fillRect(0, 0, 1, 1);
  const d = ctx.getImageData(0, 0, 1, 1).data;
  const alpha = (d[3] ?? 255) / 255;
  const rgba = `rgba(${d[0] ?? 0}, ${d[1] ?? 0}, ${d[2] ?? 0}, ${alpha.toFixed(3)})`; // allow-hex — 임의 CSS 색상을 canvas 로 정규화하는 런타임 파서

  _cache.set(key, rgba);
  return rgba;
}

export function invalidateTokenCache(): void {
  _cache.clear();
}
